import { useMemo } from 'react'
import { msg } from '@lingui/core/macro'
import { MessageDescriptor } from '@lingui/core'
import { useMutation } from '@apollo/client/react'
import { AdminAPI, GraphQL } from '@guestgoodie/graphql'
import { Delete01Icon } from '@hugeicons/core-solid-rounded'
import { Trans, useLingui } from '@lingui/react/macro'

import { useCloseModal } from '@/Router'
import Button from '@/components/ui/Button'
import ConfirmDialog from '@/components/ui/ConfirmDialog'

const syncableObjectMapping: Record<
  GraphQL.IntegrationTypes,
  GraphQL.SyncableObject[]
> = {
  [GraphQL.IntegrationTypes.Pms]: [
    GraphQL.SyncableObject.Rooms,
    GraphQL.SyncableObject.Stays
  ],
  [GraphQL.IntegrationTypes.BookingEngine]: [
    GraphQL.SyncableObject.BookableServices,
    GraphQL.SyncableObject.Bookings
  ]
}

const objectToLabel: Record<GraphQL.SyncableObject, MessageDescriptor> = {
  [GraphQL.SyncableObject.BookableServices]: msg`Services that can be booked`,
  [GraphQL.SyncableObject.Bookings]:
    msg`Existing bookings for the current guests`,
  [GraphQL.SyncableObject.Rooms]: msg`Rooms`,
  [GraphQL.SyncableObject.Stays]: msg`Reservations/Stays with Guests`
}

export function ConnectedView({
  feature
}: {
  feature: GraphQL.IntegrationFeatureFragmentFragment
}) {
  const { i18n, t } = useLingui()
  const closeModal = useCloseModal()

  const syncableObjects = useMemo(() => {
    return syncableObjectMapping[feature.type]
  }, [feature.type])

  const syncs = useMemo(() => {
    return syncableObjects.map(object => {
      const isSyncing = feature.syncing?.includes(object)
      const msg = objectToLabel[object]

      return {
        label: i18n._(msg),
        object,
        isSyncing
      }
    })
  }, [syncableObjects, feature, i18n])

  const provider = feature.provider
  const type = feature.type

  const [sync] = useMutation(AdminAPI.integration.SYNC_FEATURE, {
    refetchQueries: [
      AdminAPI.integration.GET_ALL_FEATURES,
      {
        query: AdminAPI.integration.GET_FEATURE,
        variables: { id: feature.id }
      }
    ]
  })

  const [remove, { loading }] = useMutation(
    AdminAPI.integration.DELETE_FEATURE,
    {
      refetchQueries: [AdminAPI.integration.GET_ALL_FEATURES],
      update(cache) {
        cache.evict({
          id: cache.identify({
            __typename: 'IntegrationFeature',
            id: feature.id
          })
        })

        cache.evict({
          id: cache.identify({
            __typename: 'IntegrationConnector',
            provider: feature.provider
          })
        })

        cache.gc()
      },
      onCompleted: closeModal
    }
  )

  return (
    <div className="flex flex-col gap-5 p-2.5">
      <div className="flex flex-col gap-2.5 p-2.5">
        <h2 className="text-text text-heading2">
          <Trans>Your Integration is connected and working.</Trans>
        </h2>
        <p className="text-text text-regular">
          <Trans>Currently connected with: {provider}</Trans>
        </p>
        <div className="flex flex-col gap-2">
          <h2>
            <Trans>
              This Integration can sync the following objects. Normally you
              don&apos;t need to do anything since GuestGoodie syncs
              automatically.
            </Trans>
          </h2>
          <div className="flex flex-col gap-1">
            {syncs.map((s, idx) => (
              <div className="flex flex-row items-center gap-2" key={idx}>
                <span className="text-regular text-text">{s.label}</span>
                <Button
                  onClick={() => sync({ variables: { type: s.object } })}
                  loading={s.isSyncing}
                >
                  <Trans>Sync</Trans>
                </Button>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="flex flex-row justify-end gap-2">
        <ConfirmDialog
          title={t`Uninstall Integration?`}
          message={t`Are you sure that you want to uninstall the integration with "${provider}" of type "${type}"? You will keep all data, but they are not synced anymore.`}
          onConfirm={() => remove({ variables: { id: feature.id } })}
          asChild
        >
          <Button
            loading={loading}
            type="button"
            variant="outline"
            Icon={Delete01Icon}
          >
            <Trans>Uninstall</Trans>
          </Button>
        </ConfirmDialog>
      </div>
    </div>
  )
}
